import React, { useEffect, useState } from "react";
import { agentApi } from "../../services/agentApi";

const STATUS_LABEL = {
  CONNECTED: "conectado",
  DISCONNECTED: "desconectado",
  BANNED: "banido",
};

export default function StartAgentModal({ contact, contacts, onClose, onStarted }) {
  const [channels, setChannels] = useState([]);
  const [channelId, setChannelId] = useState("");
  const [mode, setMode] = useState("automatic");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [bulkResult, setBulkResult] = useState(null);

  const targets = contacts?.length ? contacts : contact ? [contact] : [];
  const bulk = targets.length > 1;

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { channels: list } = await agentApi.channels.list();
        if (!active) return;
        const all = list || [];
        setChannels(all);
        const preferred =
          all.find((c) => c.isDefault && c.status === "CONNECTED") ||
          all.find((c) => c.status === "CONNECTED");
        setChannelId(preferred?.id || "");
      } catch (err) {
        if (active) setError(err?.message || "Erro ao carregar canais");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!channelId) {
      setError("Selecione um número/canal conectado.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      if (bulk) {
        const r = await agentApi.sessions.createBulk({
          leadIds: targets.map((item) => item.leadId),
          channelId,
          mode,
        });
        setBulkResult(r);
        onStarted?.(null, mode, r);
        return;
      }
      const r = await agentApi.sessions.create({
        leadId: targets[0].leadId,
        channelId,
        mode,
      });
      onStarted(r.sessionId, r.mode || mode);
    } catch (err) {
      const data = err?.response?.data;
      const apiError = typeof data?.error === "string" ? data.error : "";
      const msg = apiError || err?.message || "";
      if (msg.includes("active session"))
        setError("Já existe uma conversa ativa para esse lead.");
      else if (msg.includes("opted out"))
        setError("Esse telefone está em opt-out.");
      else if (msg.includes("phone or processId"))
        setError("Lead precisa ter telefone e número de processo.");
      else if (msg.includes("connected channel"))
        setError("Nenhum canal WhatsApp conectado.");
      else if (msg.includes("active playbook"))
        setError("Nenhum playbook ativo configurado.");
      else if (msg.includes("queue unavailable"))
        setError(
          data?.message ||
            "Fila Redis indisponível. Suba o Redis e reinicie o agent-worker.",
        );
      else setError(`Erro: ${msg || "desconhecido"}`);
    } finally {
      setSubmitting(false);
    }
  }

  const connectedChannels = channels.filter((c) => c.status === "CONNECTED");

  return (
    <div
      className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
      onClick={(e) => {
        e.stopPropagation();
        onClose();
      }}
    >
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-md border border-gray-200"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="text-gray-900 font-semibold">
            {bulk ? "Iniciar agente em lote" : "Iniciar conversa via WhatsApp"}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-700 text-xl leading-none"
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div className="text-sm text-gray-600">
            {bulk ? (
              <>
                <span className="font-medium text-gray-900">
                  {targets.length} contatos
                </span>
                <ul className="mt-2 max-h-28 overflow-y-auto space-y-1 text-gray-800">
                  {targets.slice(0, 8).map((item) => (
                    <li key={item.leadId}>
                      {item.name}
                      {item.primaryPhone ? ` • ${item.primaryPhone}` : ""}
                    </li>
                  ))}
                  {targets.length > 8 && (
                    <li className="text-gray-500">e mais {targets.length - 8}</li>
                  )}
                </ul>
                {targets.some((item) => item.inContact) && (
                  <p className="mt-2 text-xs text-amber-700">
                    Alguns contatos já estão em atendimento. O agente será iniciado mesmo assim.
                  </p>
                )}
              </>
            ) : (
              <>
                Contato:{" "}
                <span className="font-medium text-gray-900">{targets[0]?.name}</span>
                {targets[0]?.primaryPhone && (
                  <span className="text-gray-500"> • {targets[0].primaryPhone}</span>
                )}
              </>
            )}
          </div>

          {bulkResult && (
            <div className="text-sm space-y-2">
              <p className="text-emerald-700">
                {(bulkResult.started || []).length} conversa(s) iniciada(s).
              </p>
              {(bulkResult.failed || []).length > 0 && (
                <div className="text-red-700 bg-red-50 border border-red-200 rounded-md p-2 space-y-1">
                  <p>{bulkResult.failed.length} não iniciada(s):</p>
                  <ul className="max-h-28 overflow-y-auto">
                    {bulkResult.failed.map((item) => (
                      <li key={item.leadId}>
                        {item.name || item.leadId}: {item.error}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Iniciar a partir do número (WhatsApp Wone)
            </label>
            {loading ? (
              <div className="text-gray-500 text-sm">Carregando números...</div>
            ) : connectedChannels.length === 0 ? (
              <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-2">
                Nenhum número WhatsApp conectado. Conecte um número em
                Configurações → Canais WhatsApp.
              </div>
            ) : (
              <select
                value={channelId}
                onChange={(e) => setChannelId(e.target.value)}
                className="w-full px-3 py-2 bg-white border border-gray-300 text-gray-900 rounded-md text-sm focus:outline-none focus:border-blue-500"
              >
                {connectedChannels.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.label} — {c.phoneNumber}
                    {c.isDefault ? " (canal padrão)" : ""}
                  </option>
                ))}
              </select>
            )}
            <p className="text-xs text-gray-400 mt-1">
              A conversa sai pelo número selecionado, em nome de "Wone Ativos
              Judiciais".
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tipo de atendimento
            </label>
            <div className="space-y-2">
              <label
                className={`flex items-start gap-3 p-3 rounded-md border cursor-pointer transition ${
                  mode === "automatic"
                    ? "border-emerald-500 bg-emerald-50"
                    : "border-gray-200 hover:bg-gray-50"
                }`}
              >
                <input
                  type="radio"
                  name="attendanceMode"
                  value="automatic"
                  checked={mode === "automatic"}
                  onChange={() => setMode("automatic")}
                  className="mt-1 text-emerald-600 focus:ring-emerald-500"
                />
                <span>
                  <span className="block text-sm font-medium text-gray-900">
                    Automático (agente)
                  </span>
                  <span className="block text-xs text-gray-500 mt-0.5">
                    O bot inicia o playbook e conduz a conversa sozinho. Você
                    pode assumir depois em Conversas.
                  </span>
                </span>
              </label>

              <label
                className={`flex items-start gap-3 p-3 rounded-md border cursor-pointer transition ${
                  mode === "manual"
                    ? "border-blue-500 bg-blue-50"
                    : "border-gray-200 hover:bg-gray-50"
                }`}
              >
                <input
                  type="radio"
                  name="attendanceMode"
                  value="manual"
                  checked={mode === "manual"}
                  onChange={() => setMode("manual")}
                  className="mt-1 text-blue-600 focus:ring-blue-500"
                />
                <span>
                  <span className="block text-sm font-medium text-gray-900">
                    Manual (trader)
                  </span>
                  <span className="block text-xs text-gray-500 mt-0.5">
                    Você assume desde o início. Digite a primeira mensagem em
                    Conversas — o bot não envia nada automaticamente.
                  </span>
                </span>
              </label>
            </div>
          </div>

          {error && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-2">
              {error}
            </div>
          )}

          {bulkResult ? (
            <button
              type="button"
              onClick={onClose}
              className="w-full py-2 bg-gray-900 hover:bg-gray-800 text-white rounded-md font-medium"
            >
              Fechar
            </button>
          ) : (
          <button
            type="submit"
            disabled={submitting || loading || connectedChannels.length === 0 || targets.length === 0}
            className={`w-full py-2 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-md font-medium transition-colors ${
              mode === "manual"
                ? "bg-blue-600 hover:bg-blue-700"
                : "bg-emerald-600 hover:bg-emerald-700"
            }`}
          >
            {submitting
              ? "Iniciando..."
              : bulk
                ? `Iniciar agente para ${targets.length} contatos`
              : mode === "manual"
                ? "Iniciar atendimento manual"
                : "Iniciar agente automático"}
          </button>
          )}
        </form>
      </div>
    </div>
  );
}
